import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { TeamMember } from '../types';
import { ApiService } from '../services/api';
import { teamMembers } from '../utils/data';

interface AuthContextType {
  isAuthenticated: boolean;
  logout: () => void;
  loginWithGoogle: (user: any) => void;
  currentUser: string | null;
  currentTeamMember: TeamMember | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [currentTeamMember, setCurrentTeamMember] = useState<TeamMember | null>(null);

  // Helper to load user from localStorage
  const loadUser = useCallback(() => {
    const user = localStorage.getItem('user');
    console.log('[AuthContext] loadUser: localStorage user =', user);
    if (user) {
      setIsAuthenticated(true);
      try {
        const parsed = JSON.parse(user);
        setCurrentUser(parsed.email || parsed.name || null);
        console.log('[AuthContext] Authenticated as', parsed.email || parsed.name);
      } catch {
        setCurrentUser(null);
        console.log('[AuthContext] Failed to parse user');
      }
    } else {
      setIsAuthenticated(false);
      setCurrentUser(null);
      setCurrentTeamMember(null);
      console.log('[AuthContext] No user found, not authenticated');
    }
  }, []);

  useEffect(() => {
    loadUser();
    console.log('[AuthContext] useEffect: isAuthenticated =', isAuthenticated, 'currentUser =', currentUser);
    // Listen for login/logout events from other tabs
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'user') {
        loadUser();
      }
    };
    window.addEventListener('storage', onStorage);
    // Listen for custom login/logout events in this tab
    const onAuthEvent = () => loadUser();
    window.addEventListener('auth-event', onAuthEvent);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('auth-event', onAuthEvent);
    };
  }, [loadUser, currentUser, isAuthenticated]);

  const loginWithGoogle = async (user: any) => {
    localStorage.setItem('user', JSON.stringify(user));
    setIsAuthenticated(true);
    setCurrentUser(user.email || user.name || null);
    
    // Fetch team member data if user has an email
    if (user.email) {
      try {
        const response = await ApiService.getTeamMemberByEmail(user.email);
        if (response.success && response.data) {
          setCurrentTeamMember(response.data);
          console.log('[AuthContext] Found team member:', response.data);
        } else {
          console.log('[AuthContext] No team member found for email:', user.email);
          // Fall back to local team member data
          const localTeamMember = teamMembers.find(member => member.email === user.email);
          if (localTeamMember) {
            setCurrentTeamMember(localTeamMember);
            console.log('[AuthContext] Using local team member data:', localTeamMember);
          }
        }
      } catch (error) {
        console.error('[AuthContext] Error fetching team member:', error);
        // Fall back to local team member data
        const localTeamMember = teamMembers.find(member => member.email === user.email);
        if (localTeamMember) {
          setCurrentTeamMember(localTeamMember);
          console.log('[AuthContext] Using local team member data after error:', localTeamMember);
        }
      }
    }
    
    // Notify all listeners
    window.dispatchEvent(new Event('auth-event'));
  };

  const logout = () => {
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setCurrentUser(null);
    setCurrentTeamMember(null);
    window.dispatchEvent(new Event('auth-event'));
  };

  const value = {
    isAuthenticated,
    logout,
    loginWithGoogle,
    currentUser,
    currentTeamMember,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 